/**
 * FASE 13 — Step wizard "Documenti precontrattuali IVASS" (versione unificata).
 *
 * Sostituisce il vecchio IvassConsentsStep con:
 * - 1 solo grande bottone "📄 Consulta documentazione precontrattuale"
 * - Al click apre modale unica con lista scrollabile documenti raggruppati
 *   (Colucci trasversali + docs compagnia filtrati per modulo/wizard)
 * - Ogni documento: card con titolo/descrizione + Anteprima HTML + Scarica PDF
 * - Footer modale: 1 sola checkbox globale obbligatoria + bottone Conferma
 * - Alla conferma: chiama POST /public/ivass/consents/accept-bulk con l'array
 *   di TUTTI i document_ids obbligatori (audit trail cumulativo + granulare
 *   lato DB gestito dal backend)
 *
 * Props:
 *   groupFilter: array di group keys (es. ['colucci_trasversali','compagnia_aig'])
 *   value: { accepted: bool, batch_id: string|null, document_ids: [] }
 *   onChange: (newValue) => void
 *   submissionId: str | null   (se null: la registrazione avviene post-submit
 *                               tramite il helper registerConsentsBulk chiamato
 *                               dal wizard con il submissionId ottenuto)
 *   module: "do" | "cyber" | "fine_arts"
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  FileText, ExternalLink, Check, AlertCircle, Loader2, X, ShieldCheck,
  Eye, Download, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PreviewModal({ doc, onClose }) {
  if (!doc) return null;
  return (
    <div
      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ivass-preview-modal"
    >
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-4 border-b border-[#E5E2DB] flex items-center justify-between shrink-0 bg-[#F8F4ED]">
          <div className="min-w-0">
            <h3 className="font-heading text-lg text-[#0F1F14] truncate">{doc.title}</h3>
            {doc.subtitle && <p className="text-xs text-[#4A5E51] truncate">{doc.subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            data-testid="ivass-preview-close"
            className="p-1.5 rounded-lg hover:bg-white/60 shrink-0"
          >
            <X className="w-5 h-5 text-[#4A5E51]" />
          </button>
        </header>
        <div
          className="p-6 overflow-y-auto prose prose-sm max-w-none"
          data-testid="ivass-preview-content"
          dangerouslySetInnerHTML={{ __html: doc.content_html || "<em>Nessun contenuto disponibile per l'anteprima.</em>" }}
        />
      </div>
    </div>
  );
}

function DocRow({ doc, onPreview }) {
  return (
    <div
      data-testid={`ivass-doc-row-${doc.id}`}
      className="flex items-start gap-3 py-3 border-b border-[#E5E2DB] last:border-b-0"
    >
      <div className="mt-0.5 w-8 h-8 shrink-0 rounded-full bg-[#D9C49A]/30 flex items-center justify-center">
        <FileText className="w-4 h-4 text-[#163321]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-[#0F1F14] leading-tight">
          {doc.title}
          {doc.mandatory_for_submission && (
            <span className="ml-2 text-[10px] text-red-700 font-medium">obbligatorio</span>
          )}
        </p>
        {doc.description && (
          <p className="text-xs text-[#4A5E51] mt-1 line-clamp-2">{doc.description}</p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {doc.content_html && (
            <button
              type="button"
              onClick={() => onPreview(doc)}
              data-testid={`ivass-doc-preview-btn-${doc.id}`}
              className="text-[11px] px-2.5 py-1 rounded border border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5 inline-flex items-center gap-1"
            >
              <Eye className="w-3 h-3" /> Anteprima
            </button>
          )}
          {(doc.download_url || doc.url) && (
            <a
              href={`${API.replace(/\/api$/, "")}${doc.download_url || doc.url}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`ivass-doc-download-btn-${doc.id}`}
              className="text-[11px] px-2.5 py-1 rounded border border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5 inline-flex items-center gap-1"
            >
              <Download className="w-3 h-3" /> Scarica PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentsModal({
  open, onClose, groups, documents, onConfirm, confirming,
}) {
  const [previewDoc, setPreviewDoc] = useState(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (!open) {
      setChecked(false);
      setPreviewDoc(null);
    }
  }, [open]);
  if (!open) return null;

  const mandatoryCount = documents.filter((d) => d.mandatory_for_submission).length;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ivass-docs-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="p-5 border-b border-[#E5E2DB] shrink-0 bg-gradient-to-br from-[#F8F4ED] to-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#163321]" />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-[#4A5E51]">
                  Reg. IVASS 40/2018
                </span>
              </div>
              <h2 className="font-heading text-xl text-[#0F1F14] leading-tight">
                Documentazione precontrattuale
              </h2>
              <p className="text-xs text-[#4A5E51] mt-1">
                {mandatoryCount} documenti obbligatori da prendere in visione
                prima dell'invio della richiesta.
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="ivass-docs-modal-close"
              className="p-2 rounded-lg hover:bg-white/60 shrink-0"
            >
              <X className="w-5 h-5 text-[#4A5E51]" />
            </button>
          </div>
        </header>

        {/* Body scrollabile con gruppi accordion aperti */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="ivass-docs-modal-body">
          {Object.entries(groups).map(([groupKey, group]) => {
            const groupDocs = (group.documents || [])
              .map((did) => documents.find((d) => d.id === did))
              .filter(Boolean);
            if (!groupDocs.length) return null;
            return (
              <section
                key={groupKey}
                data-testid={`ivass-modal-group-${groupKey}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-4 rounded-full bg-[#C9A448]" />
                  <h4 className="font-heading text-[13px] uppercase tracking-wider text-[#163321]">
                    {group.title}
                  </h4>
                  <span className="text-[10px] text-[#4A5E51]">
                    ({groupDocs.length} doc)
                  </span>
                </div>
                <div className="rounded-lg border border-[#E5E2DB] bg-white/60 px-4 py-1">
                  {groupDocs.map((d) => (
                    <DocRow key={d.id} doc={d} onPreview={setPreviewDoc} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer con checkbox unica */}
        <footer className="border-t border-[#E5E2DB] p-5 shrink-0 bg-[#FCFCFA]">
          <label
            className="flex items-start gap-3 cursor-pointer mb-4 group"
            data-testid="ivass-bulk-consent-label"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="ivass-bulk-consent-checkbox"
              className="mt-1 w-5 h-5 accent-[#163321] cursor-pointer shrink-0"
            />
            <span className="text-sm text-[#0F1F14] leading-relaxed">
              <strong>Confermo di aver preso visione</strong> della
              documentazione precontrattuale IVASS resa disponibile
              (Privacy GDPR, Informativa Intermediario, DIP e Fascicoli
              informativi delle compagnie mandanti) e accetto quanto previsto
              dalla stessa.
            </span>
          </label>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="ivass-modal-cancel-btn"
              className="px-4 py-2 rounded-full text-sm text-[#4A5E51] hover:bg-slate-100"
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={!checked || confirming}
              onClick={() => onConfirm()}
              data-testid="ivass-bulk-confirm-btn"
              className={`px-5 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${
                !checked || confirming
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-[#163321] text-white hover:bg-[#0F1F14]"
              }`}
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvataggio…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Conferma e prosegui
                </>
              )}
            </button>
          </div>
        </footer>
      </div>
      {previewDoc && (
        <PreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}

export function IvassConsentsStepUnified({
  groupFilter = null,
  value,
  onChange,
  module,
}) {
  const [documents, setDocuments] = useState(null);
  const [groups, setGroups] = useState({});
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/ivass/documents`)
      .then((r) => {
        setDocuments(r.data.documents);
        setGroups(r.data.groups);
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  // Filtra gruppi visibili in base al modulo (D&O → colucci; Cyber → cyber +
  // colucci; Fine Arts → fine_arts + colucci)
  const visibleGroups = useMemo(() => {
    if (!groupFilter) return groups;
    const out = {};
    for (const gk of groupFilter) {
      if (groups[gk]) out[gk] = groups[gk];
    }
    return out;
  }, [groups, groupFilter]);

  const visibleDocIds = useMemo(() => {
    const s = new Set();
    Object.values(visibleGroups).forEach((g) =>
      (g.documents || []).forEach((d) => s.add(d))
    );
    return s;
  }, [visibleGroups]);

  const visibleDocs = useMemo(
    () => (documents || []).filter((d) => visibleDocIds.has(d.id)),
    [documents, visibleDocIds]
  );

  const mandatoryIds = useMemo(
    () =>
      visibleDocs.filter((d) => d.mandatory_for_submission).map((d) => d.id),
    [visibleDocs]
  );

  const accepted = !!value?.accepted;

  const handleConfirm = async () => {
    // FASE 13: registrazione POSTPONED al submit finale.
    // Qui salviamo solo lo stato locale (accepted + document_ids) — il
    // wizard chiamerà `registerConsentsBulk` con submissionId reale.
    setConfirming(true);
    try {
      const allIds = visibleDocs.map((d) => d.id);
      onChange({
        accepted: true,
        document_ids: allIds,
        mandatory_ids: mandatoryIds,
        accepted_locally_at: new Date().toISOString(),
      });
      setModalOpen(false);
    } finally {
      setConfirming(false);
    }
  };

  if (err) {
    return (
      <div
        className="bg-red-50 border border-red-200 rounded-lg p-4"
        data-testid="ivass-load-error"
      >
        <AlertCircle className="w-5 h-5 text-red-600 mb-2" />
        <p className="text-sm text-red-800">
          Errore caricamento documenti IVASS: {err}
        </p>
      </div>
    );
  }

  if (!documents) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="ivass-loading"
      >
        <Loader2 className="w-8 h-8 animate-spin text-[#163321]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ivass-consents-step-unified">
      <div className="bg-gradient-to-br from-[#D9C49A]/15 to-[#F8F4ED] border border-[#D9C49A]/40 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 shrink-0 rounded-full bg-[#163321] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#C9A448]" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#4A5E51] mb-1">
              Reg. IVASS 40/2018
            </p>
            <h3 className="font-heading text-lg text-[#0F1F14] leading-tight">
              Documentazione precontrattuale
            </h3>
            <p className="text-sm text-[#4A5E51] mt-1">
              Prima di inviare la richiesta prendi visione dei documenti
              obbligatori (Privacy, Informativa Intermediario, DIP e Fascicoli
              informativi delle compagnie).
            </p>
          </div>
        </div>

        {!accepted ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="ivass-open-docs-btn"
            className="w-full px-5 py-4 rounded-xl bg-[#163321] text-white font-medium inline-flex items-center justify-center gap-2 hover:bg-[#0F1F14] transition-colors shadow-sm"
          >
            <FileText className="w-5 h-5" />
            Consulta documentazione precontrattuale
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <div
            className="rounded-xl border-2 border-emerald-600 bg-emerald-50/60 p-4 flex items-start gap-3"
            data-testid="ivass-accepted-summary"
          >
            <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-600 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm text-[#0F1F14]">
                Documentazione consultata e accettata
              </p>
              <p className="text-xs text-[#4A5E51] mt-1">
                Hai preso visione di {value.document_ids?.length || 0}{" "}
                documenti IVASS. La registrazione definitiva
                (audit trail + firma cumulativa SHA256) sarà completata
                al momento dell'invio della richiesta.
              </p>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid="ivass-reopen-docs-btn"
                className="mt-2 text-xs text-[#163321] underline hover:no-underline"
              >
                Riapri per rivedere
              </button>
            </div>
          </div>
        )}
      </div>

      <DocumentsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        groups={visibleGroups}
        documents={visibleDocs}
        onConfirm={handleConfirm}
        confirming={confirming}
      />
    </div>
  );
}

/**
 * Helper: registrazione bulk POST-submit del consenso.
 * Chiamato dal wizard con il `submissionId` ottenuto dopo il submit.
 */
export async function registerConsentsBulk({
  submissionId, documentIds, module,
}) {
  if (!submissionId || !Array.isArray(documentIds) || !documentIds.length) {
    return { total: 0, batch_id: null, error: "missing_params" };
  }
  try {
    const r = await axios.post(`${API}/public/cyber/consents/accept-bulk`, {
      submission_id: submissionId,
      document_ids: documentIds,
      module: module || null,
      accepted: true,
    });
    return {
      total: r.data.total_documents,
      batch_id: r.data.batch_id,
      aggregated_sha256_short: r.data.aggregated_sha256_short,
    };
  } catch (e) {
    return { total: 0, batch_id: null, error: e.response?.data?.detail || e.message };
  }
}
